import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Globe, FileText, Briefcase, Clock, DollarSign, Target, ClipboardList, Plus } from 'lucide-react';
import { doc, updateDoc, collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import {
  FaceFrownIcon,
  FaceSmileIcon,
  FireIcon,
  HandThumbUpIcon,
  HeartIcon,
  PaperClipIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import { Listbox } from '@headlessui/react';
import CreateLoanApplicationModal from './CreateLoanApplicationModal';

// Add this utility function
function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const moods = [
  { name: 'Excited', value: 'excited', icon: FireIcon, iconColor: 'text-white', bgColor: 'bg-red-500' },
  { name: 'Loved', value: 'loved', icon: HeartIcon, iconColor: 'text-white', bgColor: 'bg-pink-400' },
  { name: 'Happy', value: 'happy', icon: FaceSmileIcon, iconColor: 'text-white', bgColor: 'bg-green-400' },
  { name: 'Sad', value: 'sad', icon: FaceFrownIcon, iconColor: 'text-white', bgColor: 'bg-yellow-400' },
  { name: 'Thumbsy', value: 'thumbsy', icon: HandThumbUpIcon, iconColor: 'text-white', bgColor: 'bg-blue-500' },
  { name: 'I feel nothing', value: null, icon: XMarkIcon, iconColor: 'text-gray-400', bgColor: 'bg-transparent' },
];

const LenderMatchCard = ({
  id,
  businessName,
  businessWebsite,
  businessDescription,
  industry,
  experience,
  requestedAmount,
  useOfFunds,
  useOfFundsDescription,
  businessPlan,
  financialProjections,
  generatingRevenue,
  revenue,
  status = 'pending', // pending, matched, declined
  onClick,
  activity = [],
  callPickup,
  callNotes,
  meetingDate,
  textSent,
  emailSent,
  email,
  firstName,
  lastName,
  phoneNumber,
}) => {
  const navigate = useNavigate();
  const [currentStatus, setCurrentStatus] = useState(status);
  const [selected, setSelected] = useState(moods[5]);
  const [comment, setComment] = useState('');
  const [isLoanApplicationModalOpen, setIsLoanApplicationModalOpen] = useState(false);

  const handleStatusChange = async (e) => {
    e.stopPropagation();
    const newStatus = e.target.value;
    try {
      const loanDocRef = doc(db, 'lenderMatches', id);
      await updateDoc(loanDocRef, {
        status: newStatus
      });
      setCurrentStatus(newStatus);
    } catch (error) {
      console.error('Error updating match status:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'matched':
        return 'bg-green-100 text-green-800';
      case 'declined':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!comment.trim()) return;

    try {
      const newActivity = {
        type: 'commented',
        person: {
          name: 'Current User', // Replace with actual user name
          imageUrl: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
        },
        comment,
        date: 'Just now',
        dateTime: new Date().toISOString(),
        mood: selected.value,
      };

      // Add to Firestore
      await addDoc(collection(db, `lenderMatches/${id}/activity`), newActivity);
      setComment('');
      // You might want to refresh the activity feed here
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const formatMeetingDate = (date) => {
    if (!date) return '';
    
    // Handle Firebase Timestamp
    if (date instanceof Timestamp) {
      return date.toDate().toLocaleString();
    }
    
    // Handle string date format
    if (typeof date === 'string') {
      return date;
    }
    
    return 'Invalid date';
  };

  // Create a combined activity array that includes call status and other activities
  const getCombinedActivity = () => {
    const combinedActivity = [];  // Start with empty array

    // Add text/email status if call wasn't picked up
    if (!callPickup && textSent && emailSent) {
      combinedActivity.push({  // Changed from unshift to push
        id: 'communication-sent',
        type: 'communication',
        person: { name: 'System' },
        status: 'Text and email sent',
        date: 'Recent',
        dateTime: new Date().toISOString(),
      });
    }

    // Add call status activity
    if (callPickup !== undefined) {
      combinedActivity.push({  // Changed from unshift to push
        id: 'call-status',
        type: 'status',
        person: { name: 'System' },
        status: callPickup ? 'Call picked up' : 'Call not picked up',
        date: 'Recent',
        dateTime: new Date().toISOString(),
      });

      // If call was picked up, add call notes and meeting time
      if (callPickup && callNotes) {
        combinedActivity.push({  // Changed from unshift to push
          id: 'call-notes',
          type: 'notes',
          person: { name: 'System' },
          notes: callNotes,
          date: 'Recent',
          dateTime: new Date().toISOString(),
        });

        if (meetingDate) {
          combinedActivity.push({  // Changed from unshift to push
            id: 'meeting-date',
            type: 'meeting',
            person: { name: 'System' },
            meetingDate,
            date: 'Recent',
            dateTime: new Date().toISOString(),
          });
        }
      }
    }

    // Add existing activity at the end
    return [...combinedActivity, ...activity];
  };

  const handleOpenModal = (e) => {
    e.stopPropagation();
    if (!id) {
      console.error('No lender match ID found');
      alert('Error: Could not find lender match ID');
      return;
    }
    setIsLoanApplicationModalOpen(true);
  };

  return (
    <>
      <div 
        className="w-full max-w-2xl rounded-xl overflow-hidden shadow-lg border border-gray-200 bg-white cursor-pointer hover:shadow-xl transition-shadow"
        onClick={onClick}
      >
        <div className="p-6">
          {/* Header Section */}
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-500" />
                {businessName}
              </h3>
              <a 
                href={businessWebsite}
                className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1 mt-1"
                onClick={(e) => e.stopPropagation()}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Globe className="w-4 h-4" />
                {businessWebsite}
              </a>
            </div>
            <select
              value={currentStatus}
              onChange={handleStatusChange}
              onClick={(e) => e.stopPropagation()}
              className={`px-4 py-2 rounded-full font-medium ${getStatusColor(currentStatus)}`}
            >
              <option value="pending">Pending</option>
              <option value="matched">Matched</option>
              <option value="declined">Declined</option>
            </select>
          </div>

          {/* Business Details */}
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-700 mb-2">Business Description</h4>
              <p className="text-gray-600">{businessDescription}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="text-sm text-gray-500">Industry</p>
                  <p className="font-medium">{industry}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="text-sm text-gray-500">Experience</p>
                  <p className="font-medium">{experience}</p>
                </div>
              </div>
            </div>

            {/* Financial Information */}
            <div className="bg-blue-50 p-4 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-blue-500" />
                  <p className="text-gray-700">Requested Amount</p>
                </div>
                <p className="font-semibold">${Number(requestedAmount).toLocaleString()}</p>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-blue-500" />
                  <p className="text-gray-700">Use of Funds</p>
                </div>
                <p className="font-medium">{useOfFunds}</p>
              </div>

              <div className="pt-2 border-t border-blue-100">
                <p className="text-sm text-gray-600">{useOfFundsDescription}</p>
              </div>
            </div>

            {/* Additional Information */}
            <div className="grid grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
              <div>
                <p className="text-sm text-gray-500">Business Plan</p>
                <p className="font-medium">{businessPlan || 'None'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Financial Projections</p>
                <p className="font-medium">{financialProjections ? 'Yes' : 'No'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Revenue</p>
                <p className="font-medium">${Number(revenue).toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              // Add Newity functionality here
            }}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add to Newity
          </button>
          <button
            onClick={handleOpenModal}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Loan Application
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              // Add email functionality here
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ClipboardList className="w-4 h-4" />
            Email This Lead
          </button>
        </div>

        <div className="border-t border-gray-200 px-6 py-4">
          <ul role="list" className="space-y-6">
            {getCombinedActivity().map((activityItem, activityItemIdx) => (
              <li key={activityItem.id} className="relative flex gap-x-4">
                <div className={classNames(
                  activityItemIdx === activity.length - 1 ? 'h-6' : '-bottom-6',
                  'absolute left-0 top-0 flex w-6 justify-center'
                )}>
                  <div className="w-px bg-gray-200" />
                </div>
                
                {activityItem.type === 'commented' ? (
                  <>
                    <img
                      src={activityItem.person.imageUrl}
                      alt=""
                      className="relative mt-3 h-6 w-6 flex-none rounded-full bg-gray-50"
                    />
                    <div className="flex-auto rounded-md p-3 ring-1 ring-inset ring-gray-200">
                      <div className="flex justify-between gap-x-4">
                        <div className="py-0.5 text-xs leading-5 text-gray-500">
                          <span className="font-medium text-gray-900">{activityItem.person.name}</span> commented
                        </div>
                        <time dateTime={activityItem.dateTime} className="flex-none py-0.5 text-xs leading-5 text-gray-500">
                          {activityItem.date}
                        </time>
                      </div>
                      <p className="text-sm leading-6 text-gray-500">{activityItem.comment}</p>
                    </div>
                  </>
                ) : activityItem.type === 'status' ? (
                  <>
                    <div className="relative flex h-6 w-6 flex-none items-center justify-center bg-white">
                      <div className={classNames(
                        'h-1.5 w-1.5 rounded-full',
                        activityItem.status.includes('picked up') ? 'bg-green-100 ring-1 ring-green-300' : 'bg-red-100 ring-1 ring-red-300'
                      )} />
                    </div>
                    <p className="flex-auto py-0.5 text-xs leading-5 text-gray-500">
                      {activityItem.status}
                    </p>
                    <time dateTime={activityItem.dateTime} className="flex-none py-0.5 text-xs leading-5 text-gray-500">
                      {activityItem.date}
                    </time>
                  </>
                ) : activityItem.type === 'notes' ? (
                  <>
                    <div className="relative flex h-6 w-6 flex-none items-center justify-center bg-white">
                      <FileText className="h-4 w-4 text-gray-400" />
                    </div>
                    <div className="flex-auto rounded-md p-3 ring-1 ring-inset ring-gray-200">
                      <p className="text-sm leading-6 text-gray-500">{activityItem.notes}</p>
                    </div>
                  </>
                ) : activityItem.type === 'meeting' ? (
                  <>
                    <div className="relative flex h-6 w-6 flex-none items-center justify-center bg-white">
                      <Clock className="h-4 w-4 text-blue-500" />
                    </div>
                    <p className="flex-auto py-0.5 text-xs leading-5 text-gray-500">
                      Meeting scheduled for: {formatMeetingDate(activityItem.meetingDate)}
                    </p>
                  </>
                ) : activityItem.type === 'communication' ? (
                  <>
                    <div className="relative flex h-6 w-6 flex-none items-center justify-center bg-white">
                      <div className="h-1.5 w-1.5 rounded-full bg-blue-100 ring-1 ring-blue-300" />
                    </div>
                    <p className="flex-auto py-0.5 text-xs leading-5 text-gray-500">
                      {activityItem.status}
                    </p>
                    <time dateTime={activityItem.dateTime} className="flex-none py-0.5 text-xs leading-5 text-gray-500">
                      {activityItem.date}
                    </time>
                  </>
                ) : (
                  <>
                    <div className="relative flex h-6 w-6 flex-none items-center justify-center bg-white">
                      {activityItem.type === 'paid' ? (
                        <CheckCircleIcon className="h-6 w-6 text-indigo-600" aria-hidden="true" />
                      ) : (
                        <div className="h-1.5 w-1.5 rounded-full bg-gray-100 ring-1 ring-gray-300" />
                      )}
                    </div>
                    <p className="flex-auto py-0.5 text-xs leading-5 text-gray-500">
                      <span className="font-medium text-gray-900">{activityItem.person.name}</span> {activityItem.type}
                    </p>
                    <time dateTime={activityItem.dateTime} className="flex-none py-0.5 text-xs leading-5 text-gray-500">
                      {activityItem.date}
                    </time>
                  </>
                )}
              </li>
            ))}
          </ul>

          {/* Comment form */}
          <div className="mt-6 flex gap-x-3" onClick={e => e.stopPropagation()}>
            <img
              src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
              alt=""
              className="h-6 w-6 flex-none rounded-full bg-gray-50"
            />
            <form action="#" className="relative flex-auto" onSubmit={handleCommentSubmit}>
              <div className="overflow-hidden rounded-lg pb-12 shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-indigo-600">
                <label htmlFor="comment" className="sr-only">
                  Add your comment
                </label>
                <textarea
                  rows={2}
                  name="comment"
                  id="comment"
                  className="block w-full resize-none border-0 bg-transparent py-1.5 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  placeholder="Add your comment..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </div>

              <div className="absolute inset-x-0 bottom-0 flex justify-between py-2 pl-3 pr-2">
                <div className="flex items-center space-x-5">
                  <div className="flex items-center">
                    <button
                      type="button"
                      className="-m-2.5 flex h-10 w-10 items-center justify-center rounded-full text-gray-400 hover:text-gray-500"
                    >
                      <PaperClipIcon className="h-5 w-5" aria-hidden="true" />
                      <span className="sr-only">Attach a file</span>
                    </button>
                  </div>
                  <div className="flex items-center">
                    <Listbox value={selected} onChange={setSelected}>
                      {({ open }) => (
                        <>
                          <Listbox.Label className="sr-only">Your mood</Listbox.Label>
                          <div className="relative">
                            <Listbox.Button className="relative -m-2.5 flex h-10 w-10 items-center justify-center rounded-full text-gray-400 hover:text-gray-500">
                              <span className="flex items-center justify-center">
                                {selected.value === null ? (
                                  <span>
                                    <FaceSmileIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                                    <span className="sr-only">Add your mood</span>
                                  </span>
                                ) : (
                                  <span>
                                    <span
                                      className={classNames(
                                        selected.bgColor,
                                        'flex h-8 w-8 items-center justify-center rounded-full'
                                      )}
                                    >
                                      <selected.icon className="h-5 w-5 flex-shrink-0 text-white" aria-hidden="true" />
                                    </span>
                                    <span className="sr-only">{selected.name}</span>
                                  </span>
                                )}
                              </span>
                            </Listbox.Button>

                            <Listbox.Options className="absolute bottom-10 z-10 -ml-6 w-60 rounded-lg bg-white py-3 text-base shadow ring-1 ring-black ring-opacity-5 focus:outline-none sm:ml-auto sm:w-64 sm:text-sm">
                              {moods.map((mood) => (
                                <Listbox.Option
                                  key={mood.value}
                                  className={({ active }) =>
                                    classNames(
                                      active ? 'bg-gray-100' : 'bg-white',
                                      'relative cursor-default select-none px-3 py-2'
                                    )
                                  }
                                  value={mood}
                                >
                                  <div className="flex items-center">
                                    <div
                                      className={classNames(
                                        mood.bgColor,
                                        'flex h-8 w-8 items-center justify-center rounded-full'
                                      )}
                                    >
                                      <mood.icon
                                        className={classNames(mood.iconColor, 'h-5 w-5 flex-shrink-0')}
                                        aria-hidden="true"
                                      />
                                    </div>
                                    <span className="ml-3 block truncate font-medium">{mood.name}</span>
                                  </div>
                                </Listbox.Option>
                              ))}
                            </Listbox.Options>
                          </div>
                        </>
                      )}
                    </Listbox>
                  </div>
                </div>
                <button
                  type="submit"
                  className="rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  Comment
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {isLoanApplicationModalOpen && (
        <CreateLoanApplicationModal
          isOpen={isLoanApplicationModalOpen}
          onClose={() => setIsLoanApplicationModalOpen(false)}
          onSuccess={() => {
            alert('Loan application updated successfully!');
          }}
          lenderMatchData={{
            id,
            businessName,
            email,
            firstName,
            lastName,
            phoneNumber,
            revenue,
            requestedAmount,
            useOfFundsDescription,
            experience
          }}
        />
      )}
    </>
  );
};

export default LenderMatchCard;